<template>
  <div class="children-settings-container">
    <div class="content-header">
      <div class="header-icon">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
          <circle cx="12" cy="7" r="4"/>
        </svg>
      </div>
      <h1>Gyerekek</h1>
      <p>{{ isTeacher && !isAdmin ? 'Hozzárendelt csoportok gyerekei' : 'Gyerekek listázása és kezelése' }}</p>
    </div>

    <div class="filters-section">
      <div class="search-group">
        <div class="search-input-wrapper">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8"/>
            <path d="M21 21l-4.35-4.35"/>
          </svg>
          <input 
            type="text" 
            class="modern-input" 
            placeholder="Keresés név alapján..." 
            v-model="searchQuery"
            @keyup.enter="fetchGroups(currentPage)"
            @input="fetchGroups(currentPage)"
          />
        </div>
      </div>

      <div class="filter-options">
        <div class="checkbox-group">
          <input 
            type="checkbox" 
            v-model="dispInactiveOnly"  
            @change="fetchGroups(currentPage)" 
            id="isInactive"
            class="modern-checkbox"
          >
          <label for="isInactive" class="checkbox-label">
            <span class="checkbox-custom"></span>
            Inaktív gyerekek kijelzése
          </label>
        </div>
      </div>
    </div>

    <div class="table-section">
      <div class="table-header">
        <h3>Gyerekek listája</h3>
        <span class="count-badge">{{ children.length }} gyerek</span>
      </div>
      
      <div class="modern-table-container">
        <table class="modern-table">
          <thead>
            <tr>
              <th>
                <div class="th-content">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                  Vezetéknév
                </div>
              </th>
              <th>
                <div class="th-content">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                  Keresztnév
                </div>
              </th>
              <th>
                <div class="th-content">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                  Csoport
                </div>
              </th>
              <th>
                <div class="th-content">Versenyző</div>
              </th>
              <th>
                <div class="th-content">Műveletek</div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="child in children"
              :key="child.id"
              class="table-row"
              @click="goToUniqueChildPage(child.id)"
            >
              <td class="name-cell">
                <div class="cell-content">
                  <div class="child-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                      <circle cx="12" cy="7" r="4"/>
                    </svg>
                  </div>
                  <span class="child-name">{{ child.family_name }}</span>
                </div>
              </td>
              <td class="name-cell">
                <span class="child-name">{{ child.sure_name }}</span>
              </td>
              <td class="group-cell">
                <div class="groups-cell">
                  <span v-if="!child.groups || child.groups.length === 0" class="group-badge group-badge--empty">Nincs csoport</span>
                  <div v-else class="groups-list">
                    <span
                      v-for="group in child.groups.slice(0, 2)"
                      :key="group.group_id"
                      :class="['group-badge', { 'group-badge--primary': group.is_primary }]"
                    >
                      {{ group.group_name }}
                      <span v-if="group.is_primary" class="primary-indicator">*</span>
                    </span>
                    <span v-if="child.groups.length > 2" class="group-badge group-badge--more">
                      +{{ child.groups.length - 2 }} több
                    </span>
                  </div>
                </div>
              </td>
              <td class="competitor-cell">
                <label class="toggle-switch">
                  <input type="checkbox" :checked="child.is_competitor" @change="toggleCompetitor(child.id, $event.target.checked)" />
                  <span class="toggle-slider"></span>
                </label>
              </td>
              <td class="actions-cell">
                <button class="action-btn assign-btn" @click.stop="openAssignGroupModal(child)" title="Csoport hozzárendelése">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                  Csoport
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="pagination-section">
      <div class="pagination-info">
        <span>Oldal {{ currentPage }} / {{ totalPages }}</span>
      </div>
      <div class="pagination-controls">
        <button 
          class="pagination-btn" 
          :class="{ disabled: currentPage === 1 }"
          @click="fetchGroups(currentPage - 1)"
          :disabled="currentPage === 1"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="15,18 9,12 15,6"/>
          </svg>
          Előző
        </button>
        <button 
          class="pagination-btn" 
          :class="{ disabled: currentPage === totalPages }"
          @click="fetchGroups(currentPage + 1)"
          :disabled="currentPage === totalPages"
        >
          Következő
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="9,18 15,12 9,6"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- Assign Group mdl -->
    <div v-if="showAssignGroupModal" class="mdl-overlay" @click="closeAssignGroupModal">
      <div class="mdl-content" @click.stop>
        <div class="mdl-header">
          <h3>Csoport hozzárendelés - {{ selectedChildForGroup ? selectedChildForGroup.family_name + ' ' + selectedChildForGroup.sure_name : '' }}</h3>
          <button class="close-btn" @click="closeAssignGroupModal">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <div class="mdl-body">
          <div class="form-group">
            <label>Csoport kiválasztása:</label>
            <select v-model="selectedGroupToAssign" class="modern-select">
              <option value="">Válassz csoportot...</option>
              <option v-for="group in availableGroups" :key="group.id" :value="group.id" :disabled="isChildInGroup(selectedChildForGroup, group.id)">
                {{ group.name }}
              </option>
            </select>
          </div>
          <div class="form-group">
            <label class="checkbox-label">
              <input type="checkbox" v-model="isPrimaryForAssignment" class="modern-checkbox" />
              <span class="checkbox-custom"></span>
              Elsődleges csoport
            </label>
          </div>
          <div class="mdl-actions">
            <button class="modern-btn modern-btn--success" :disabled="!selectedGroupToAssign" @click="assignChildToGroup">Hozzárendelés</button>
            <button class="modern-btn" @click="closeAssignGroupModal">Mégse</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
  
  <script>
  export default {
    data() {
      return {
        children: [],
        totalPages: 0,
        currentPage: 1,
        limit: 10,
        searchQuery: '',
        tableSize: -2,
        dispInactiveOnly: false,
        isAdmin: false,
        isTeacher: false,
        userId: null,
        showAssignGroupModal: false,
        selectedChildForGroup: null,
        availableGroups: [],
        selectedGroupToAssign: '',
        isPrimaryForAssignment: false
      };
    },
    methods: {
      getIsAdmin() {
        this.isAdmin = this.$isAdmin();
      },
      getIsTeacher() {
        this.isTeacher = this.$isTeacher();
      },
      getUserId() {
        this.userId = this.$getUserId();
      },
      async fetchTableSize() {
        try {
          const response = await fetch(`${this.$BACKEND_HOST}/api/settings/children/tableinfo`);
          const data = await response.json();
          if(data.message == "done") 
          {
            this.totalPages = parseInt(data.tableSize/this.limit) + 1;
          }
        } catch (error) {
          console.error('Error fetching groups:', error);
        }
      },
      async fetchGroups(page) {
        this.currentPage = page;
        var searchedStatus = this.dispInactiveOnly ? "Inactive" : "Active";
        try {
          // First, get all children data
          const response = await fetch(`${this.$BACKEND_HOST}/api/settings/children/info?page=${this.currentPage}&limit=${this.limit}&search=${this.searchQuery}&status=${searchedStatus}`);
          const data = await response.json();
          
          // Backend handles the filtering logic correctly for both admin and teacher users
          this.children = data.data || [];
        } catch (error) {
          console.error('Error fetching children:', error);
        }
      },
      async toggleCompetitor(childId, isChecked) {
        try {
          await fetch(`${this.$BACKEND_HOST}/api/children/${childId}/competitor`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ is_competitor: isChecked }),
          });
          const child = this.children.find(c => c.id === childId);
          if (child) child.is_competitor = isChecked;
        } catch (error) {
          console.error('Error updating competitor status:', error);
        }
      },
      goToUniqueChildPage(id) {

        this.$router.push(`/children/${id}`);
      },
      async openAssignGroupModal(child) {
        this.selectedChildForGroup = child;
        this.selectedGroupToAssign = '';
        this.isPrimaryForAssignment = !child.groups || child.groups.length === 0 || !child.groups.some(g => g.is_primary);
        if (this.availableGroups.length === 0) {
          await this.fetchAvailableGroups();
        }
        this.showAssignGroupModal = true;
      },
      closeAssignGroupModal() {
        this.showAssignGroupModal = false;
        this.selectedChildForGroup = null;
        this.selectedGroupToAssign = '';
        this.isPrimaryForAssignment = false;
      },
      async fetchAvailableGroups() {
        try {
          const response = await fetch(`${this.$BACKEND_HOST}/api/settings/groups/info`);
          const data = await response.json();
          this.availableGroups = data.groups || [];
          
          if (this.isTeacher && !this.isAdmin && this.userId) {
            const teacherResponse = await fetch(`${this.$BACKEND_HOST}/api/settings/teachers/groups?teacher_id=${this.userId}`);
            if (teacherResponse.ok) {
              const teacherData = await teacherResponse.json();
              if (teacherData.groups && Array.isArray(teacherData.groups)) {
                const assignedGroupIds = teacherData.groups.map(g => g.id);
                this.availableGroups = this.availableGroups.filter(group => assignedGroupIds.includes(group.id));
              }
            }
          }
        } catch (error) {
          console.error('Error fetching groups:', error);
        }
      },
      isChildInGroup(child, groupId) {
        if (!child || !child.groups) return false;
        return child.groups.some(g => g.group_id === groupId);
      },
      async assignChildToGroup() {
        if (!this.selectedGroupToAssign || !this.selectedChildForGroup) return;
        try {
          const response = await fetch(`${this.$BACKEND_HOST}/api/child/groups/add`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              child_id: this.selectedChildForGroup.id,
              group_id: parseInt(this.selectedGroupToAssign),
              is_primary: this.isPrimaryForAssignment
            })
          });
          if (response.ok) {
            await this.fetchGroups(this.currentPage);
            this.closeAssignGroupModal();
          } else {
            console.error('Failed to assign group');
          }
        } catch (error) {
          console.error('Error assigning group:', error);
        }
      }
    },
    mounted() {
      this.getIsAdmin();
      this.getIsTeacher();
      this.getUserId();
      this.fetchTableSize();
      this.fetchGroups(this.currentPage); // Initial fetch
    }

    
  };
  </script>
  
  <style scoped>
  .children-settings-container {
    padding: 2rem;
    max-width: 1200px;
    margin: 0 auto;
    background: var(--color-background);
    min-height: 100vh;
  }

  .content-header {
    text-align: center;
    margin-bottom: 2rem;
    padding: 2rem;
    background: var(--card-bg);
    border-radius: 16px;
    box-shadow: var(--shadow-sm);
  }

  .header-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    background: var(--button-primary);
    border-radius: 16px;
    margin-bottom: 1rem;
    color: white;
  }

  .content-header h1 {
    font-size: 2.5rem;
    font-weight: 700;
    color: var(--color-text);
    margin: 0 0 0.5rem 0;
  }

  .content-header p {
    color: var(--color-text-muted);
    font-size: 1.1rem;
    margin: 0;
  }

  .filters-section {
    display: flex;
    gap: 1.5rem;
    margin-bottom: 2rem;
    flex-wrap: wrap;
    align-items: flex-end;
  }

  .search-group {
    flex: 1;
    min-width: 300px;
  }

  .search-input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
  }

  .search-input-wrapper svg {
    position: absolute;
    left: 1rem;
    color: var(--text-muted);
    z-index: 1;
  }

  .modern-input {
    width: 100%;
    padding: 0.875rem 1rem 0.875rem 3rem;
    border: 2px solid var(--color-border);
    border-radius: 12px;
    background: var(--card-bg);
    color: var(--color-text);
    font-size: 1rem;
    transition: all 0.2s ease;
  }

  .modern-input:focus {
    outline: none;
    border-color: var(--primary-blue);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  .modern-input::placeholder {
    color: var(--text-muted);
  }

  .filter-options {
    display: flex;
    align-items: center;
  }

  .checkbox-group {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .modern-checkbox {
    display: none;
  }

  .checkbox-label {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    cursor: pointer;
    font-weight: 500;
    color: var(--color-text);
    user-select: none;
  }

  .checkbox-custom {
    width: 20px;
    height: 20px;
    border: 2px solid var(--color-border);
    border-radius: 6px;
    background: var(--card-bg);
    position: relative;
    transition: all 0.2s ease;
  }

  .modern-checkbox:checked + .checkbox-label .checkbox-custom {
    background: var(--primary-blue);
    border-color: var(--primary-blue);
  }

  .modern-checkbox:checked + .checkbox-label .checkbox-custom::after {
    content: '';
    position: absolute;
    left: 6px;
    top: 2px;
    width: 6px;
    height: 10px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }

  .table-section {
    background: var(--card-bg);
    border-radius: 16px;
    box-shadow: var(--shadow-sm);
    overflow: hidden;
    margin-bottom: 2rem;
  }

  .table-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem 2rem;
    border-bottom: 1px solid var(--color-border);
    background: var(--bg-secondary);
  }

  .table-header h3 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--color-text);
  }

  .count-badge {
    background: var(--button-primary);
    color: white;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-size: 0.875rem;
    font-weight: 600;
  }

  .modern-table-container {
    overflow-x: auto;
  }

  .modern-table {
    width: 100%;
    border-collapse: collapse;
  }

  .modern-table th {
    background: var(--bg-secondary);
    padding: 1rem 1.5rem;
    text-align: left;
    font-weight: 600;
    color: var(--color-text);
    border-bottom: 1px solid var(--color-border);
    position: sticky;
    top: 0;
    z-index: 10;
  }

  .th-content {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .th-content svg {
    color: var(--primary-blue);
  }

  .table-row {
    transition: all 0.2s ease;
    cursor: pointer;
  }

  .table-row:hover {
    background: var(--hover-bg);
  }

  .table-row td {
    padding: 1rem 1.5rem;
    border-bottom: 1px solid var(--color-border);
    vertical-align: middle;
  }

  .name-cell {
    font-weight: 500;
  }

  .cell-content {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .child-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    background: var(--button-primary);
    border-radius: 8px;
    color: white;
    flex-shrink: 0;
  }

  .child-name {
    color: var(--color-text);
    font-weight: 500;
  }

  .group-cell {
    text-align: center;
  }

  .competitor-cell {
    text-align: center;
  }

  .actions-cell {
    text-align: center;
  }

  .action-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--color-border);
    border-radius: 8px;
    background: var(--card-bg);
    color: var(--color-text);
    font-size: 0.85rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .action-btn:hover {
    border-color: var(--primary-blue);
    color: var(--primary-blue);
    background: var(--hover-bg);
  }

  .assign-btn {
    border-color: var(--success);
    color: var(--success);
  }

  .assign-btn:hover {
    background: var(--success);
    color: white;
  }

  .mdl-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }

  .mdl-content {
    background: var(--card-bg);
    border-radius: 16px;
    box-shadow: var(--shadow-lg);
    width: 90%;
    max-width: 500px;
    max-height: 90vh;
    overflow-y: auto;
  }

  .mdl-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem;
    border-bottom: 1px solid var(--color-border);
  }

  .mdl-header h3 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--color-text);
  }

  .close-btn {
    background: none;
    border: none;
    color: var(--color-text-muted);
    cursor: pointer;
    padding: 0.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    transition: all 0.2s ease;
  }

  .close-btn:hover {
    background: var(--hover-bg);
    color: var(--color-text);
  }

  .mdl-body {
    padding: 1.5rem;
  }

  .mdl-body .form-group {
    margin-bottom: 1.25rem;
  }

  .mdl-body label {
    display: block;
    font-weight: 600;
    color: var(--color-text);
    margin-bottom: 0.5rem;
  }

  .mdl-actions {
    display: flex;
    gap: 0.75rem;
    justify-content: flex-end;
    margin-top: 1.5rem;
  }

  .toggle-switch {
    position: relative;
    display: inline-block;
    width: 44px;
    height: 24px;
  }

  .toggle-switch input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  .toggle-slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ccc;
    transition: 0.3s;
    border-radius: 24px;
  }

  .toggle-slider:before {
    position: absolute;
    content: "";
    height: 18px;
    width: 18px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    transition: 0.3s;
    border-radius: 50%;
  }

  .toggle-switch input:checked + .toggle-slider {
    background-color: var(--primary-blue);
  }

  .toggle-switch input:checked + .toggle-slider:before {
    transform: translateX(20px);
  }

  .group-badge {
    display: inline-block;
    background: var(--success);
    color: white;
    padding: 0.375rem 0.75rem;
    border-radius: 12px;
    font-size: 0.875rem;
    font-weight: 500;
  }

  .pagination-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem 2rem;
    background: var(--card-bg);
    border-radius: 16px;
    box-shadow: var(--shadow-sm);
  }

  .pagination-info {
    color: var(--color-text-muted);
    font-weight: 500;
  }

  .pagination-controls {
    display: flex;
    gap: 0.75rem;
  }

  .pagination-btn {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.25rem;
    background: var(--card-bg);
    border: 2px solid var(--color-border);
    border-radius: 12px;
    color: var(--color-text);
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .pagination-btn:hover:not(.disabled) {
    background: var(--hover-bg);
    border-color: var(--primary-blue);
    color: var(--primary-blue);
  }

  .pagination-btn.disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .pagination-btn svg {
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    .children-settings-container {
      padding: 1rem;
    }

    .filters-section {
      flex-direction: column;
      align-items: stretch;
    }

    .search-group {
      min-width: auto;
    }

    .pagination-section {
      flex-direction: column;
      gap: 1rem;
      text-align: center;
    }

    .pagination-controls {
      justify-content: center;
    }

    .modern-table th,
    .table-row td {
      padding: 0.75rem 1rem;
    }

    .table-header {
      padding: 1rem 1.5rem;
    }
  }
  </style>
  